<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { useRouter, useRoute } from 'vue-router';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper.js';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import { useExactTimestamp } from 'shared/composables/useExactTimestamp';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import CardMessagePreview from './CardMessagePreview.vue';
import CardMessagePreviewWithMeta from './CardMessagePreviewWithMeta.vue';
import CardPriorityIcon from './CardPriorityIcon.vue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  contact: {
    type: Object,
    required: true,
  },
  stateInbox: {
    type: Object,
    required: true,
  },
  accountLabels: {
    type: Array,
    required: true,
  },
});

const exactTimestamp = useExactTimestamp();

const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const cardMessagePreviewWithMetaRef = ref(null);

const currentContact = computed(() => props.contact);

const currentContactName = computed(() => currentContact.value?.name);
const currentContactThumbnail = computed(() => currentContact.value?.thumbnail);
const currentContactStatus = computed(
  () => currentContact.value?.availabilityStatus
);

const inbox = computed(() => props.stateInbox);

const inboxName = computed(() => inbox.value?.name);

const inboxIcon = computed(() => {
  const { channelType, medium, voiceEnabled } = inbox.value;
  return getInboxIconByType(channelType, medium, 'fill', voiceEnabled);
});

const lastActivityAt = computed(() => {
  const timestamp = props.conversation?.timestamp;
  return timestamp ? shortTimestamp(dynamicTime(timestamp)) : '';
});

const hasVisibleLabels = computed(() => {
  const { labels = [] } = props.conversation;
  return props.accountLabels.some(({ title }) => labels.includes(title));
});

const isMentionConversation = computed(() => {
  const {
    conversationType,
    conversation_type: conversationTypeLegacy,
    additionalAttributes = {},
    additional_attributes: additionalAttributesLegacy = {},
  } = props.conversation || {};

  return (
    conversationType === 'mention' ||
    conversationTypeLegacy === 'mention' ||
    additionalAttributes.conversationType === 'mention' ||
    additionalAttributesLegacy.conversation_type === 'mention'
  );
});

const isAwaitingReply = computed(() => Boolean(props.conversation?.waitingSince));

const priorityLabel = computed(() => {
  const priority = props.conversation?.priority;

  if (!priority) {
    return '';
  }

  return t(`CONVERSATION.PRIORITY.OPTIONS.${priority.toUpperCase()}`);
});

const showInsightBadges = computed(() => {
  return (
    isMentionConversation.value ||
    isAwaitingReply.value ||
    Boolean(priorityLabel.value)
  );
});

const showMessagePreviewWithoutMeta = computed(() => {
  return (
    !cardMessagePreviewWithMetaRef.value?.hasSlaThreshold &&
    !hasVisibleLabels.value
  );
});

const onCardClick = e => {
  const path = frontendURL(
    conversationUrl({
      accountId: route.params.accountId,
      id: props.conversation.id,
    })
  );

  if (e.metaKey || e.ctrlKey) {
    window.open(
      window.chatwootConfig.hostURL + path,
      '_blank',
      'noopener noreferrer nofollow'
    );
    return;
  }
  router.push({ path });
};
</script>

<template>
  <div
    role="button"
    class="flex w-full gap-3 px-3 py-4 transition-all duration-300 ease-in-out cursor-pointer"
    @click="onCardClick"
  >
    <Avatar
      :name="currentContactName"
      :src="currentContactThumbnail"
      :size="24"
      :status="currentContactStatus"
      rounded-full
    />
    <div class="flex flex-col w-full gap-1 min-w-0">
      <div class="flex items-center justify-between h-6 gap-2">
        <h4 class="text-base font-medium truncate text-n-slate-12">
          {{ currentContactName }}
        </h4>
        <div class="flex items-center gap-2">
          <div
            v-tooltip.left="inboxName"
            class="flex items-center justify-center flex-shrink-0 rounded-full bg-n-alpha-2 size-5"
          >
            <Icon
              :icon="inboxIcon"
              class="flex-shrink-0 text-n-slate-11 size-3"
            />
          </div>
          <span
            v-tooltip.top="{
              content: exactTimestamp(conversation?.timestamp),
              delay: { show: 500, hide: 0 },
            }"
            class="text-sm text-n-slate-10"
          >
            {{ lastActivityAt }}
          </span>
        </div>
      </div>
      <div
        v-if="showInsightBadges"
        class="flex flex-wrap items-center gap-1.5"
      >
        <span
          v-if="isMentionConversation"
          class="inline-flex items-center gap-1 rounded-full border border-n-iris-6/40 bg-n-iris-3 px-2 py-0.5 text-[11px] font-semibold text-n-iris-11 dark:border-n-iris-9/40 dark:bg-n-iris-3/10 dark:text-n-iris-11"
        >
          <Icon icon="i-lucide-at-sign" class="size-3" />
          {{ $t('CHAT_LIST.BADGES.MENTION') }}
        </span>
        <span
          v-if="isAwaitingReply"
          class="inline-flex items-center gap-1 rounded-full border border-n-amber-6/40 bg-n-amber-3 px-2 py-0.5 text-[11px] font-semibold text-n-amber-11 dark:border-n-amber-9/40 dark:bg-n-amber-3/10 dark:text-n-amber-11"
        >
          <Icon icon="i-lucide-hourglass" class="size-3" />
          {{ $t('CHAT_LIST.BADGES.AWAITING_REPLY') }}
        </span>
        <span
          v-if="priorityLabel"
          class="inline-flex items-center gap-1 rounded-full border border-n-ruby-6/30 bg-n-ruby-3/70 px-2 py-0.5 text-[11px] font-semibold text-n-ruby-11 dark:border-n-ruby-9/40 dark:bg-n-ruby-3/10 dark:text-n-ruby-11"
        >
          <CardPriorityIcon
            :priority="conversation.priority || null"
            class="!text-n-ruby-11"
          />
          {{ priorityLabel }}
        </span>
      </div>
      <CardMessagePreview
        v-show="showMessagePreviewWithoutMeta"
        :conversation="conversation"
      />
      <CardMessagePreviewWithMeta
        v-show="!showMessagePreviewWithoutMeta"
        ref="cardMessagePreviewWithMetaRef"
        :conversation="conversation"
        :contact="contact"
        :account-labels="accountLabels"
        :has-labels="hasVisibleLabels"
      />
    </div>
  </div>
</template>
